import React, { useState } from "react";
import { Button, Form } from "reactstrap";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import Logo from "../../components/Logo";
import { useGoogleLogin } from "@react-oauth/google";
import Cookies from "js-cookie";
import { googleSignInAPI, resendConfirmationAPI } from "../../API/userApi";
import axios from "axios";
import { signIn, useSignInQuery } from "../../hooks/authQueries";
import { toast } from "react-toastify";
import { useLoggedInUser } from "../../config/zStore";
import ButtonLoader from "../../components/common/buttonLoader";
import { useMutation } from "@tanstack/react-query";

const Login = () => {
  const navigate = useNavigate();
  const [inputData, setInputData] = useState({ email: "", password: "" });
  const { email, password } = inputData;
  const { updateUserData } = useLoggedInUser()

  // Store a session the same way whichever method was used, so it survives a
  // reload and is attached to every API request.
  const startSession = (user, token, cookieOptions) => {
    Cookies.set("userKeys", JSON.stringify({ userToken: token }), cookieOptions)
    Cookies.set("currentUser", JSON.stringify(user), cookieOptions)
    updateUserData(user)
  }

  // Set when sign-in is refused because the address is not confirmed yet.
  const [unconfirmed, setUnconfirmed] = useState(null);
  const [resendNote, setResendNote] = useState(null);
  const resendConfirmation = async () => {
    try {
      const result = await resendConfirmationAPI(unconfirmed);
      setResendNote(result?.message || "We have emailed you a new link.");
    } catch (error) {
      setResendNote(error?.response?.data?.message || "Could not send the email. Please try again.");
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn : signIn,
    onSuccess: (data) => {
      console.log(data);

      // Both branches used to be 365 days, so "remember me" did nothing (QA
      // finding AUTH-02). Remembered sessions last 30 days, matching the
      // server; otherwise the cookie ends with the browser session.
      startSession(data.user, data?.token, remember ? { expires: 30 } : {})
      toast.success("Logged in successfully!");
      navigate("/profile/info");
    },
    onError: (error) => {
      console.log(error);
      
      if (error.response?.data?.code === "EMAIL_NOT_VERIFIED") {
        setUnconfirmed(inputData.email)
        setResendNote(null)
      } else if (error.response?.status === 403) {
        toast.error(error.response.data.message)
      } else { 
        toast.error("Invalid credentials");
      }
    },

  });
  const [fieldError, setFieldError] = useState(false)
  const [remember, setRemember] = useState(false)
  const handleOnChange = (e) => {
    const { name, value } = e.target;
    setInputData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitForm = (e) => {
    e?.preventDefault();
    const noField = !email || !password
    if (noField) {
      setFieldError(true)

      return;
    }
    console.log('mutatijng');
    
    mutate({ ...inputData, remember });
  }

  const signup = () => navigate("sign-up");
  // Sign-in asks Google only for the person's name and email. It also used to
  // ask for calendar access, a "sensitive" permission: Google then showed
  // every user a "Google hasn't verified this app" warning before they could
  // sign in. Calendar access is requested separately, by the Connect button in
  // the header, only for the people whose shoots sync to a calendar.
  const loginByGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        // The server verifies the Google token and issues a real session (QA
        // finding AUTH-01). Previously the browser decided it was signed in by
        // itself and never received a token, so the session was lost on reload
        // and every API request was refused.
        const { user, token } = await googleSignInAPI(tokenResponse.access_token);
        // Google sign-in has always been treated as "remember me".
        startSession(user, token, { expires: 30 });
        toast.success("Logged in successfully!");
        navigate("/profile");
      } catch (error) {
        const status = error?.response?.status;
        if (status === 404 && error.response.data?.needsRegistration) {
          // No account for this address yet: continue to registration with
          // the Google profile, as before.
          try {
            const profile = await axios.get("https://www.googleapis.com/oauth2/v3/userinfo", {
              headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
            });
            localStorage.setItem(
              "signInWithGoogle",
              JSON.stringify({ ...profile.data, googleToken: tokenResponse.access_token })
            );
            navigate("/signIn-with-google");
          } catch (profileError) {
            toast.error("Google login failed. Please try again.");
          }
        } else if (status === 403) {
          toast.error(error.response.data?.message || "This account cannot sign in yet.");
        } else {
          toast.error("Google login failed. Please try again.");
        }
      }
    },
    onError: () => {
      toast.error("Google login was unsuccessful.");
    },
  });

  return (
    <div className="row" style={{ maxWidth: "100%" }}>
      <div className="col-xl-6 col-lg-6 col-md-6 col-sm-12">
        <div className="padding_left">
          <div style={{ marginTop: 70 }}>
            <Logo />
          </div>
          <h4 className="heading" style={{ marginTop: 50 }}>
            Welcome back
          </h4>
          <p className="subheading" style={{ marginTop: -5 }}>
            Welcome back! Please enter your credentials
          </p>
          {unconfirmed && (
            <div className="alert alert-warning mt-3" role="alert">
              Confirm your email address first - we emailed a link to {unconfirmed} when you signed up.{" "}
              {resendNote ? (
                <div className="mt-1">{resendNote}</div>
              ) : (
                <button type="button" className="btn btn-link p-0 align-baseline" onClick={resendConfirmation}>
                  Resend confirmation email
                </button>
              )}
            </div>
          )}
          <Form onSubmit={handleSubmitForm}>
            <p className="input_lbl" style={{ marginTop: 30 }}>
              Email
            </p>
            <div className={`input_div border ${(fieldError && !email) && "border-danger"}`} style={{ marginTop: -10 }}>
              <input
                className={`input_field w-100`}
                type="email"
                placeholder="Enter your email"
                onChange={handleOnChange}
                value={email}
                name="email"
              />
            </div>
            <p className="input_lbl" style={{ marginTop: 20 }}>
              Password
            </p>
            <div
              className={`input_div border ${(fieldError && !password) && "border-danger"}`}
              style={{ marginTop: -10 }}
            >
              <input
                className="input_field w-100"
                name="password"
                type="password"
                placeholder="**********"
                onChange={handleOnChange}
                value={password}
              />
            </div>
            <div className="d-flex mt-2 justify-content-between align-items-center full_view_login">
              <button
                type="button"
                className="transparent_btn mt-1"
                onClick={() => navigate("/email-verify")}
              >
                Forgot Password
              </button>
              <label className=" d-flex align-items-center">
                <input checked={remember} onChange={() => setRemember(!remember)} className="cursor-pointer" type="checkbox" /> &nbsp;Remember Me
              </label>
            </div>
            <Button type="submit" className="submit_btn" style={{ marginTop: 10 }}>
              {isPending ? <ButtonLoader /> : "Sign In"}
            </Button>
          </Form>
          <br />
          <Button
            outline
            color="secondary"
            className="submit_btn"
            style={{ marginTop: 10 }}
            onClick={loginByGoogle}
          >
            <img
              alt=""
              src="/images/google.png"
              width={20}
              style={{ marginRight: 10, marginTop: -3 }}
            />
            Sign in with Google
          </Button>
          <div className="d-flex align-items-center justify-content-center bottom_width mt-3">
            <p className="subheading">Don’t have an account?</p>
            <p
              className="transparent_btn"
              style={{
                fontSize: 13,
                marginTop: 0,
                marginLeft: 5,
                cursor: "pointer",
              }}
              onClick={signup}
            >
              Sign up
            </p>
          </div>
        </div>
      </div>
      <div className="col-xl-6 col-lg-6 col-md-6 hide_img">
        <div className="input_banner_img" />
      </div>
    </div>
  );
};

export default Login;
